import type { AxiosResponse } from 'axios';

import { requestStarted, requestFulfilled } from '../redux/status/actions';
import { store } from '../redux/store';

interface KeySpecObject {
  param?: string;
  name?: string;
  wrap?: string;
  as?: string;
}

type KeySpec = string | KeySpecObject;
type ParamValue = string | number | boolean | object | undefined;

function parseKeySpec(
  keySpec: KeySpec,
  params: Record<string, ParamValue>
): ParamValue | Record<string, ParamValue> | undefined {
  // keySpec can be 'paramName', 'paramName:wrapKey', or an object { param, wrap }
  if (typeof keySpec === 'string') {
    if (keySpec.includes(':')) {
      const [paramName, wrapKey] = keySpec.split(':');
      if (wrapKey && paramName) {
        return { [wrapKey]: params[paramName] };
      }
      return undefined;
    }
    return params[keySpec];
  }

  if (typeof keySpec === 'object') {
    const paramName = keySpec.param ?? keySpec.name;
    const wrapKey = keySpec.wrap ?? keySpec.as;
    if (paramName && wrapKey) {
      return { [wrapKey]: params[paramName] };
    }
    if (paramName) {
      return params[paramName];
    }
    return undefined;
  }

  return undefined;
}

function buildArgs(
  paramKeys: KeySpec[],
  params: Record<string, ParamValue> | undefined,
  additionalParams: unknown[]
): unknown[] {
  const p = params ?? {};
  if (Array.isArray(paramKeys) && paramKeys.length) {
    return paramKeys.map(k => parseKeySpec(k, p)).concat(additionalParams);
  }

  // fallback: pass the first param value (if any)
  const firstValue = Object.values(p)[0];
  const baseArgs = firstValue !== undefined ? [firstValue] : [];
  return baseArgs.concat(additionalParams);
}

export interface LoaderParams {
  params?: Record<string, ParamValue>;
}

interface ApiResponse {
  data?: unknown;
}

export default function createLoader(
  apiFn:
    | ((...args: unknown[]) => Promise<AxiosResponse<unknown, unknown>>)
    | ((...args: unknown[]) => Promise<ApiResponse>),
  paramKeys: KeySpec[] = [],
  additionalParams: unknown[] = []
) {
  return async ({ params }: LoaderParams): Promise<unknown> => {
    store.dispatch(requestStarted());
    try {
      const args = buildArgs(paramKeys, params, additionalParams);
      const response = await apiFn(...args);
      const { data } = response;

      // unwrap single-item arrays for single-key loaders for convenience
      if (Array.isArray(data) && Array.isArray(paramKeys) && paramKeys.length === 1) {
        return data[0] ?? null;
      }

      return data;
    } catch (err) {
      // httpClient's response interceptor formats errors as `new Error(JSON.stringify({ status, ... }))`,
      // so the status lives inside the JSON message, not as a property on the error itself.
      let status: number | undefined;
      try {
        status = (JSON.parse(err instanceof Error ? err.message : '') as { status?: number })
          .status;
      } catch {
        status = undefined;
      }

      // 401s are left as-is for FallBackError to redirect home; only 403/404 are
      // normalized to the generic "not found" page here.
      if (status === 403 || status === 404) {
        throw new Error('ERR_NOT_FOUND');
      }
      throw err;
    } finally {
      // ensure loader finished state even on error
      try {
        store.dispatch(requestFulfilled());
      } catch (e) {
        console.error('Error during requestFulfilled dispatch:', e);
      }
    }
  };
}
